import { useMemo } from "react";

import { bpsToUiDecimals, perHundred, toTokenAmount } from "@bridgesplit/utils";

import { useTokenListContext } from "../../api/token-list";
import {
    EarnUserPosition,
    EarnUserPositionView,
    EarnVaultAllocationRow,
    EarnVaultAllocationRowView,
    EarnVaultAllocationsResponse,
    EarnVaultAllocationsResponseView,
    EarnVaultPositionResponse,
    EarnVaultPositionResponseView,
    EarnVaultRecord,
    EarnVaultRecordView,
    EarnVaultSnapshot,
    EarnVaultSnapshotView,
    EarnWithdrawalRequest,
    EarnWithdrawalRequestView
} from "../../types";
import { convertEarnExponentAccount, convertLendingVaultMetadata } from "../ui";

type GetDecimals = (mint: string) => number;

// Single canonical hub for the TokenAmount-emitting view converters. New domains add their
// `convertXxx` (pure, getDecimals-injected) here and expose it through useViewConverter(); this is
// the target useAbfTypesToUiConverter (legacy, ui.ts) migrates into one feature at a time.
export function useViewConverter() {
    const { getDecimals } = useTokenListContext();
    return useMemo(
        () => ({
            convertEarnVaultRecord: (vault: EarnVaultRecord) => convertEarnVaultRecord(vault, getDecimals),
            convertEarnVaultPositionResponse: (resp: EarnVaultPositionResponse) =>
                convertEarnVaultPositionResponseView(resp, getDecimals),
            convertEarnVaultSnapshots: (
                snapshots: EarnVaultSnapshot[],
                vault: Pick<EarnVaultRecord, "acceptedAssetMint" | "shareMint">
            ) => convertEarnVaultSnapshotList(snapshots, vault, getDecimals),
            convertEarnVaultAllocations: (resp: EarnVaultAllocationsResponse) =>
                convertEarnVaultAllocationsResponse(resp)
        }),
        [getDecimals]
    );
}

export function convertEarnVaultSnapshot(
    snapshot: EarnVaultSnapshot | null,
    shareDecimals: number,
    assetDecimals: number
): EarnVaultSnapshotView | null {
    if (!snapshot) return null;
    return {
        ...snapshot,
        shareSupply: toTokenAmount(snapshot.shareSupply, shareDecimals),
        totalAumAssets: toTokenAmount(snapshot.totalAumAssets, assetDecimals),
        pendingWithdrawalsAssets: toTokenAmount(snapshot.pendingWithdrawalsAssets, assetDecimals),
        utilizationBps: snapshot.utilizationBps == null ? null : bpsToUiDecimals(snapshot.utilizationBps, "bps"),
        displayApyBps: snapshot.displayApyBps == null ? null : bpsToUiDecimals(snapshot.displayApyBps, "bps"),
        realizedReturnBps24h:
            snapshot.realizedReturnBps24h == null ? null : bpsToUiDecimals(snapshot.realizedReturnBps24h, "bps"),
        realizedReturnBps7d:
            snapshot.realizedReturnBps7d == null ? null : bpsToUiDecimals(snapshot.realizedReturnBps7d, "bps"),
        realizedReturnBps30d:
            snapshot.realizedReturnBps30d == null ? null : bpsToUiDecimals(snapshot.realizedReturnBps30d, "bps"),
        rewardsApyBps: snapshot.rewardsApyBps == null ? null : bpsToUiDecimals(snapshot.rewardsApyBps, "bps"),
        headlineApyBps: snapshot.headlineApyBps == null ? null : bpsToUiDecimals(snapshot.headlineApyBps, "bps"),
        leverageX100: snapshot.leverageX100 == null ? null : perHundred(snapshot.leverageX100)
    };
}

export function convertEarnVaultRecord(vault: EarnVaultRecord, getDecimals: GetDecimals): EarnVaultRecordView {
    const assetDecimals = getDecimals(vault.acceptedAssetMint);
    const shareDecimals = getDecimals(vault.shareMint);
    return {
        ...vault,
        metadataJson: convertLendingVaultMetadata(vault.metadataJson, assetDecimals),
        latestSnapshot: convertEarnVaultSnapshot(vault.latestSnapshot, shareDecimals, assetDecimals),
        parsedAccountJson: { exponent: convertEarnExponentAccount(vault.parsedAccountJson.exponent) }
    };
}

function convertEarnUserPositionView(
    position: EarnUserPosition,
    vault: EarnVaultRecord,
    getDecimals: GetDecimals
): EarnUserPositionView {
    const assetDecimals = getDecimals(vault.acceptedAssetMint);
    const shareDecimals = getDecimals(vault.shareMint);
    return {
        ...position,
        shareBalance: toTokenAmount(position.shareBalance, shareDecimals),
        lpBalance: position.lpBalance === undefined ? undefined : toTokenAmount(position.lpBalance, shareDecimals),
        costBasisAssets: toTokenAmount(position.costBasisAssets, assetDecimals),
        realizedPnlAssets: toTokenAmount(position.realizedPnlAssets, assetDecimals),
        feesPaidAssets: toTokenAmount(position.feesPaidAssets, assetDecimals)
    };
}

function convertEarnWithdrawalRequestView(
    req: EarnWithdrawalRequest,
    vault: EarnVaultRecord,
    getDecimals: GetDecimals
): EarnWithdrawalRequestView {
    const shareDecimals = getDecimals(vault.shareMint);
    return {
        ...req,
        sharesRequested: toTokenAmount(req.sharesRequested, shareDecimals),
        sharesFilled: toTokenAmount(req.sharesFilled, shareDecimals),
        sharesClaimed: toTokenAmount(req.sharesClaimed, shareDecimals)
    };
}

function convertEarnVaultPositionResponseView(
    resp: EarnVaultPositionResponse,
    getDecimals: GetDecimals
): EarnVaultPositionResponseView {
    return {
        vault: convertEarnVaultRecord(resp.vault, getDecimals),
        position: resp.position ? convertEarnUserPositionView(resp.position, resp.vault, getDecimals) : undefined,
        openWithdrawalRequests: resp.openWithdrawalRequests.map((req) =>
            convertEarnWithdrawalRequestView(req, resp.vault, getDecimals)
        )
    };
}

export function convertEarnVaultSnapshotList(
    snapshots: EarnVaultSnapshot[],
    vault: Pick<EarnVaultRecord, "acceptedAssetMint" | "shareMint">,
    getDecimals: GetDecimals
): EarnVaultSnapshotView[] {
    const assetDecimals = getDecimals(vault.acceptedAssetMint);
    const shareDecimals = getDecimals(vault.shareMint);
    return snapshots.map((s) => convertEarnVaultSnapshot(s, shareDecimals, assetDecimals) as EarnVaultSnapshotView);
}

export function convertEarnVaultAllocationRow(row: EarnVaultAllocationRow): EarnVaultAllocationRowView {
    return {
        ...row,
        displayApyBps: row.displayApyBps == null ? null : bpsToUiDecimals(row.displayApyBps, "bps")
    };
}

function convertEarnVaultAllocationsResponse(resp: EarnVaultAllocationsResponse): EarnVaultAllocationsResponseView {
    return { ...resp, items: resp.items.map(convertEarnVaultAllocationRow) };
}
